package com.sweetlime.bingeamigo.presentation.components

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.outlined.Info
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.sweetlime.bingeamigo.model.NextEpisodeCardState
import com.sweetlime.bingeamigo.model.TvShow
import com.sweetlime.bingeamigo.presentation.ui.AccentRoles
import io.kamel.image.KamelImage
import io.kamel.image.asyncPainterResource
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

@Composable
fun HeroUpNextCard(
    card: NextEpisodeCardState,
    accent: AccentRoles,
    isNewEpisode: Boolean,
    onMarkWatched: () -> Unit,
    onOpenShow: (TvShow) -> Unit,
    modifier: Modifier = Modifier,
    heightDp: Int = 320,
) {
    val backdropUrl = "https://image.tmdb.org/t/p/w780${card.tvShow.backdropPath}"
    val season = "S${card.seasonNumber.toString().padStart(2, '0')}"
    val episode = "E${card.episodeNumber.toString().padStart(2, '0')}"
    val progress = if (card.totalThisSeason > 0)
        card.watchedThisSeason.toFloat() / card.totalThisSeason.toFloat() else 0f
    val done = card.watchedThisSeason >= card.totalThisSeason

    @OptIn(ExperimentalTime::class)
    val todayStr = remember {
        val ms = Clock.System.now().toEpochMilliseconds()
        val days = (ms / 86_400_000L).toInt()
        var n = days + 719468
        val era = if (n >= 0) n else n - 146096
        val doe = era % 146097
        val yoe = (doe - doe / 1460 + doe / 36524 - doe / 146096) / 365
        val y = yoe + (era / 146097) * 400
        val doy = doe - (365 * yoe + yoe / 4 - yoe / 100)
        val mp = (5 * doy + 2) / 153
        val d = doy - (153 * mp + 2) / 5 + 1
        val m = if (mp < 10) mp + 3 else mp - 9
        val year = if (m <= 2) y + 1 else y
        "${year}-${m.toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}"
    }
    val isFuture = remember(card.airDate) { card.airDate.length == 10 && card.airDate > todayStr }
    val airDateLabel = remember(card.airDate) {
        if (card.airDate.length == 10) {
            val months = listOf("Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec")
            val m = card.airDate.substring(5, 7).toIntOrNull() ?: 0
            val d = card.airDate.substring(8, 10).toIntOrNull() ?: 0
            "Airs ${months.getOrNull(m - 1) ?: ""} $d"
        } else ""
    }

    Box(
        modifier = modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp)
            .clip(RoundedCornerShape(18.dp))
            .height(heightDp.dp)
            .clickable { onOpenShow(card.tvShow) },
    ) {
        KamelImage(
            resource = asyncPainterResource(backdropUrl),
            contentDescription = card.tvShow.name,
            contentScale = ContentScale.Crop,
            modifier = Modifier.fillMaxSize(),
        )
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(
                    Brush.verticalGradient(
                        0.28f to Color.Transparent,
                        0.62f to Color(0x8C0A0A0C),
                        1.0f to Color(0xF50C0C0E),
                    )
                )
        )

        Column(
            modifier = Modifier
                .align(Alignment.BottomStart)
                .fillMaxWidth()
                .padding(18.dp),
        ) {
            Surface(color = accent.tag, shape = RoundedCornerShape(6.dp)) {
                Text(
                    text = if (isNewEpisode) "NEW EPISODE" else "UP NEXT",
                    color = accent.tagInk,
                    fontWeight = FontWeight.ExtraBold,
                    fontSize = 11.sp,
                    letterSpacing = 0.8.sp,
                    modifier = Modifier.padding(horizontal = 9.dp, vertical = 4.dp),
                )
            }
            Spacer(Modifier.height(9.dp))
            Text(
                text = card.tvShow.name,
                color = Color.White,
                fontWeight = FontWeight.ExtraBold,
                fontSize = 27.sp,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
            Spacer(Modifier.height(5.dp))
            Text(
                text = "$season$episode  ·  ${card.episodeName}",
                color = Color.White.copy(alpha = 0.72f),
                fontSize = 14.sp,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )

            Spacer(Modifier.height(14.dp))
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                Text("Season ${card.seasonNumber}", color = Color.White.copy(alpha = 0.66f), fontSize = 12.5.sp, fontWeight = FontWeight.SemiBold)
                Text("${card.watchedThisSeason} / ${card.totalThisSeason} eps", color = Color.White.copy(alpha = 0.66f), fontSize = 12.5.sp, fontWeight = FontWeight.SemiBold)
            }
            Spacer(Modifier.height(7.dp))
            LinearProgressIndicator(
                progress = { progress },
                modifier = Modifier.fillMaxWidth().height(5.dp).clip(RoundedCornerShape(4.dp)),
                color = accent.progress,
                trackColor = Color.White.copy(alpha = 0.18f),
            )

            Spacer(Modifier.height(16.dp))
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(9.dp)) {
                Surface(
                    onClick = onMarkWatched,
                    enabled = !done && !card.isMarking && !isFuture,
                    shape = RoundedCornerShape(13.dp),
                    color = when {
                        done || isFuture -> Color.White.copy(alpha = 0.14f)
                        else -> accent.action
                    },
                    modifier = Modifier.weight(1f).height(46.dp),
                ) {
                    Row(Modifier.fillMaxSize(), horizontalArrangement = Arrangement.Center, verticalAlignment = Alignment.CenterVertically) {
                        if (card.isMarking) {
                            CircularProgressIndicator(Modifier.size(16.dp), color = accent.actionInk, strokeWidth = 2.dp)
                        } else if (isFuture) {
                            Text(airDateLabel, color = Color.White.copy(alpha = 0.5f), fontWeight = FontWeight.ExtraBold, fontSize = 15.sp)
                        } else {
                            Icon(Icons.Filled.Check, null, tint = if (done) Color.White else accent.actionInk, modifier = Modifier.size(18.dp))
                            Spacer(Modifier.width(7.dp))
                            Text(if (done) "Season done" else "Mark watched", color = if (done) Color.White else accent.actionInk, fontWeight = FontWeight.ExtraBold, fontSize = 15.sp)
                        }
                    }
                }
                Surface(
                    onClick = { onOpenShow(card.tvShow) },
                    shape = RoundedCornerShape(13.dp),
                    color = Color.White.copy(alpha = 0.13f),
                    modifier = Modifier.size(46.dp),
                ) {
                    Box(contentAlignment = Alignment.Center) {
                        Icon(Icons.Outlined.Info, "Details", tint = Color.White, modifier = Modifier.size(20.dp))
                    }
                }
            }
        }
    }
}
